

import {
  Box,
  Button,
  HStack,
  Text,
  Textarea,
  useColorModeValue,
  IconButton,
  Select,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import axios from 'axios';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FiBold,
  FiItalic,
  FiUnderline,
  FiLink,
  FiList,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
} from 'react-icons/fi';
import Card from 'components/card/Card';
import DOMPurify from 'dompurify';

export default function PrivacyPolicy() {
  const LOCAL_STORAGE_KEY = 'privacyPolicyCache';

  // Keep the latest content in localStorage so it survives tab refreshes while the
  // API call in useEffect finishes or if the network briefly fails.
  const [content, setContent] = React.useState(() =>
    localStorage.getItem(LOCAL_STORAGE_KEY) || ''
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const textareaRef = React.useRef(null);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem('token');

  // NAYA API ENDPOINT (According to your controller/route)
  const apiUrl = `${baseUrl?.replace(/\/?$/, '/') }api/admin/privacy/privacy-policy`;

  // 1. Fetch Privacy Policy content on mount
  const fetchPrivacyPolicy = async () => {
    setLoading(true);
    try {
      if (!baseUrl || !token) {
        throw new Error('Missing base URL or authentication token');
      }

      const { data } = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let receivedContent =
        // Common shape: {status:true, data:{content:'...'}}
        data?.data?.content ??
        // Sometimes nested: {data:{privacyPolicy:{content:'...'}}}
        data?.data?.privacyPolicy?.content ??
        // Array first element with content
        data?.data?.[0]?.content ??
        // If backend returns plain string in data
        (typeof data?.data === 'string' ? data.data : undefined) ??
        // Fallbacks when backend sends raw HTML (Content-Type: text/html)
        (typeof data === 'string' ? data : undefined) ??
        data?.content ??
        '';

      // When raw HTML page is returned, strip out surrounding <html>/<body> so only the policy shows
      if (typeof receivedContent === 'string' && receivedContent.includes('<body')) {
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(receivedContent, 'text/html');
          receivedContent = doc.body?.innerHTML || receivedContent;
        } catch (_) {
          // If DOMParser fails (older browser), fall back to raw string
        }
      }

      const normalizedContent = typeof receivedContent === 'string'
        ? receivedContent
        : JSON.stringify(receivedContent);

      setContent(normalizedContent);
      localStorage.setItem(LOCAL_STORAGE_KEY, normalizedContent);
      setError(null);
    } catch (err) {
      console.error('Fetch Privacy Policy Error:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/');
      } else {
        setError(err.message || 'Failed to fetch Privacy Policy content');
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchPrivacyPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // 2. Handle saving the edited content
  const handleSave = async () => {
    try {
      const response = await axios.post(
        apiUrl,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Use returned content if the API echoes it back; otherwise keep current state
      const savedContent =
        response?.data?.data?.content ??
        response?.data?.data?.privacyPolicy?.content ??
        response?.data?.data?.[0]?.content ??
        (typeof response?.data?.data === 'string' ? response.data.data : undefined) ??
        response?.data?.content ??
        content;

      toast.success('Privacy Policy updated successfully!', {
        position: 'top-right',
        autoClose: 3000,
      });

      const normalizedSaved = typeof savedContent === 'string'
        ? savedContent
        : JSON.stringify(savedContent);

      setContent(normalizedSaved);
      localStorage.setItem(LOCAL_STORAGE_KEY, normalizedSaved);
      setIsEditing(false);
      fetchPrivacyPolicy(); // Refresh from server to stay in sync
    } catch (err) {
      console.error('Update Privacy Policy Error:', err);
      toast.error('Failed to update content', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  // 3. Handle text formatting
  const applyFormatting = (format) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.slice(start, end);

    if (!selectedText) {
      toast.warn('Please select text to format');
      return;
    }

    let newText = '';
    switch (format) {
      case 'bold': newText = `<b>${selectedText}</b>`; break;
      case 'italic': newText = `<i>${selectedText}</i>`; break;
      case 'underline': newText = `<u>${selectedText}</u>`; break;
      case 'heading1': newText = `<h1>${selectedText}</h1>`; break;
      case 'heading2': newText = `<h2>${selectedText}</h2>`; break;
      default: newText = selectedText;
    }

    const updatedContent = content.slice(0, start) + newText + content.slice(end);
    setContent(updatedContent);

    setTimeout(() => {
      textarea.selectionStart = start;
      textarea.selectionEnd = start + newText.length;
      textarea.focus();
    }, 0);
  };

  if (loading) {
    return (
      <Card flexDirection="column" w="100%" px="0px" mt="100px">
        <Flex justify="center" align="center" p="50px">
          <Spinner size="xl" />
        </Flex>
      </Card>
    );
  }

  if (error) {
    return (
      <Card flexDirection="column" w="100%" px="0px" mt="100px">
        <Text color="red.500" fontSize="18px" fontWeight="700" p="25px">
          Error: {error}
        </Text>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      mt="100px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Box px="25px" mb="20px">
        <Text color={textColor} fontSize="22px" fontWeight="700" mb="20px">
          Privacy Policy
        </Text>
        {isEditing ? (
          <Box>
            <HStack spacing={2} mb={2}>
              <Select
                placeholder="Style"
                size="sm"
                w="120px"
                onChange={(e) => {
                  if (e.target.value === 'Heading 1') applyFormatting('heading1');
                  if (e.target.value === 'Heading 2') applyFormatting('heading2');
                }}
              >
                <option value="Heading 1">Heading 1</option>
                <option value="Heading 2">Heading 2</option>
                <option value="Normal">Normal</option>
              </Select>
              <IconButton icon={<FiBold />} size="sm" aria-label="Bold" onClick={() => applyFormatting('bold')} />
              <IconButton icon={<FiItalic />} size="sm" aria-label="Italic" onClick={() => applyFormatting('italic')} />
              <IconButton icon={<FiUnderline />} size="sm" aria-label="Underline" onClick={() => applyFormatting('underline')} />
              <IconButton icon={<FiLink />} size="sm" aria-label="Link" />
              <IconButton icon={<FiList />} size="sm" aria-label="List" />
              <IconButton icon={<FiAlignLeft />} size="sm" aria-label="Align Left" />
              <IconButton icon={<FiAlignCenter />} size="sm" aria-label="Align Center" />
              <IconButton icon={<FiAlignRight />} size="sm" aria-label="Align Right" />
            </HStack>
            <Textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              minH="300px"
              placeholder="Enter Privacy Policy content here..."
              mb={4}
            />
            <HStack spacing={3}>
              <Button colorScheme="blue" onClick={handleSave}>Save</Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
            </HStack>
          </Box>
        ) : (
          <Box>
            <Box
              color={textColor}
              fontSize="md"
              lineHeight="1.8"
              mb={4}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
            />
            <Button colorScheme="teal" onClick={() => setIsEditing(true)}>
              Edit Privacy Policy
            </Button>
          </Box>
        )}
      </Box>
      <ToastContainer theme="colored" />
    </Card>
  );
}
